import nipplejs from 'nipplejs'
import { AssetsService } from '@coosmo/shared-utils'
import * as THREE from 'three'
import cameraIcon from '../assets/camera.svg'
import candleIcon from '../assets/candle.svg'
import flashlightIcon from '../assets/flashlight.svg'
import lampIcon from '../assets/lamp.svg'
import torchIcon from '../assets/torch.svg'
import { setupFullscreenButton } from './fullscreen'
import {
  applyMainLighting,
  disableAllFreeroamLights,
  getModelRadius,
  hasAnyFreeroamLightActive,
  keyStates,
  setFreeroamLight,
  toggleCandle,
  toggleFlashlight,
  toggleTorch,
} from './viewer'

const homeIcon = AssetsService.getSvgIcon('home.svg')
const fullPageIcon = AssetsService.getSvgIcon('full-screen.svg')

const DYNAMIC_LIGHTS_TOGGLE_BUTTON_ID = 'dynamic-lights-toggle'
const FLASHLIGHT_TOGGLE_BUTTON_ID = 'flashlight-toggle'
const TORCH_TOGGLE_BUTTON_ID = 'torch-toggle'
const CANDLE_TOGGLE_BUTTON_ID = 'candle-toggle'
const ENABLE_TEXTURES_BUTTON_ID = 'enable-textures'
const ENABLE_SIMPLE_MATERIAL_BUTTON_ID = 'enable-simple-material'
const ENABLE_WIREFRAME_BUTTON_ID = 'enable-wireframe'
const SNAPSHOT_BUTTON_ID = 'snapshot-button'
const FREEROAM_HELP_BUTTON_ID = 'freeroam-help-toggle'
const FREEROAM_HELP_OVERLAY_ID = 'freeroam-help-overlay'
const FREEROAM_HELP_CLOSE_ID = 'freeroam-help-close'
const FREEROAM_HELP_NEXT_ID = 'freeroam-help-next'
const SELECTED_BUTTON_CLASS = 'selected'
const LIGHTS_DISABLED_TOOLTIP = 'Not available in this mode'
const FREEROAM_LIGHT_PARAM = 'light'
const FREEROAM_LIGHT_OPTIONS = {
  '1': { buttonId: FLASHLIGHT_TOGGLE_BUTTON_ID, lightType: 'flashlight' },
  '2': { buttonId: TORCH_TOGGLE_BUTTON_ID, lightType: 'torch' },
  '3': { buttonId: CANDLE_TOGGLE_BUTTON_ID, lightType: 'candle' },
}
let joystickManager = null

import { is360ViewerDevice, isMetaQuestBrowser, isTouchDevice, isVisionProBrowser } from './utils'

export let dynamicLightsEnabled = false
export let textureDisabled = false
export let wireframeDisabled = true
export let simpleMaterialDisabled = true

if (isMetaQuestBrowser() || isVisionProBrowser()) {
  document.querySelectorAll('.btn-component-container').forEach(element => {
    element.classList.add('spatial-component')
  })
}

function createModeButtonHtml(id, icon, isSelected) {
  const selectedClass = isSelected ? SELECTED_BUTTON_CLASS : ''
  return `
    <div class="btn-component-container ${is360ViewerDevice()}">
      <button id="${id}" class="button-on-premises iconSvg buttonSpace ${selectedClass}" type="button">
        <i class="material-icons">${icon}</i>
      </button>
    </div>`
}

function shouldShowFreeroamHelp(currentMode) {
  return currentMode === 'freeroam' && !isTouchDevice()
}

function createControlsDom(currentMode, canSnapshot) {
  const fullScreenButton = `
    <div class="btn-component-container ${is360ViewerDevice()}">
      <button id="full-page" class=" iconSvg buttonSpace" type="button">
        <img src ="${fullPageIcon}" class="material-icons">
      </button>
    </div>`

  const homeButton = `
    <div class="btn-component-container ${is360ViewerDevice()}">
      <button id="home" class="button-on-premises iconSvg buttonSpace" type="button">
        <img class="material-icons" src ="${homeIcon}" alt="home">
      </button>
    </div>`

  const helpButton =
    shouldShowFreeroamHelp(currentMode)
      ? `
    <div id="freeroam-help-trigger" class="${is360ViewerDevice()}">
      <button id="${FREEROAM_HELP_BUTTON_ID}" class="titleContentToggle" type="button" aria-label="Apri istruzioni">
        <span class="freeroam-help-trigger-label" aria-hidden="true">?</span>
      </button>
    </div>`
      : ''

  const snapshotButton = canSnapshot
    ? `
    <div class="btn-component-container ${is360ViewerDevice()}">
      <button id="${SNAPSHOT_BUTTON_ID}" class="button-on-premises iconSvg buttonSpace" type="button">
        <img src="${cameraIcon}" alt="snapshot" class="material-icons">
      </button>
    </div>
  `
    : ''

  const flashlightButton = `
    <div class="btn-component-container ${is360ViewerDevice()}">
      <button id="${FLASHLIGHT_TOGGLE_BUTTON_ID}" class="button-on-premises iconSvg buttonSpace" type="button">
        <img src="${flashlightIcon}" alt="flashlight" class="material-icons">
      </button>
    </div>`

  const torchButton = `
    <div class="btn-component-container ${is360ViewerDevice()}">
      <button id="${TORCH_TOGGLE_BUTTON_ID}" class="button-on-premises iconSvg buttonSpace" type="button">
        <img src="${torchIcon}" alt="torch" class="material-icons">
      </button>
    </div>`

  const candleButton = `
    <div class="btn-component-container ${is360ViewerDevice()}">
      <button id="${CANDLE_TOGGLE_BUTTON_ID}" class="button-on-premises iconSvg buttonSpace" type="button">
        <img src="${candleIcon}" alt="candle" class="material-icons">
      </button>
    </div>`

  const dynamicLightButton = `
    <div class="btn-component-container ${is360ViewerDevice()}">
      <button id="${DYNAMIC_LIGHTS_TOGGLE_BUTTON_ID}" class="button-on-premises iconSvg buttonSpace" type="button">
        <i class="material-icons">lightbulb_outline</i>
      </button>
    </div>`

  const lightsContainer = `
    <div class="lightButton">
      ${currentMode === 'freeroam' ? flashlightButton : ''}
      ${currentMode === 'freeroam' ? torchButton : ''}
      ${currentMode === 'freeroam' ? candleButton : ''}
      ${currentMode === 'orbit' ? dynamicLightButton : ''}
    </div>`

  const controlsContainer = `
    <div id="nexus-controls-container" class="${is360ViewerDevice()}">
      <div id="nexus-controls" class="${is360ViewerDevice()}">
        <div id="modes-group">
          ${createModeButtonHtml(ENABLE_TEXTURES_BUTTON_ID, 'texture', !textureDisabled)}
          ${createModeButtonHtml(ENABLE_SIMPLE_MATERIAL_BUTTON_ID, 'square', !simpleMaterialDisabled)}
          ${createModeButtonHtml(ENABLE_WIREFRAME_BUTTON_ID, 'grid_on', !wireframeDisabled)}
        </div>
        ${lightsContainer}
        <div class="functionButton">
          ${snapshotButton}
          ${homeButton}
          ${fullScreenButton}
        </div>
      </div>
    </div>`

  const widgetContainer = document.getElementById('widgetContainer')
  if (widgetContainer) {
    widgetContainer.insertAdjacentHTML('beforeend', controlsContainer)
    if (helpButton) {
      widgetContainer.insertAdjacentHTML('beforeend', helpButton)
    }
  }

  if (shouldShowFreeroamHelp(currentMode)) {
    createFreeroamHelpOverlay()
  }

  if (currentMode === 'freeroam' && isTouchDevice()) {
    createJoystickIfMissing()
  }
}

function createFreeroamHelpOverlay() {
  const widgetContainer = document.getElementById('widgetContainer')
  const overlayExists = document.getElementById(FREEROAM_HELP_OVERLAY_ID)
  if (!widgetContainer || overlayExists) return

  const overlay = `
    <div id="${FREEROAM_HELP_OVERLAY_ID}" class="freeroam-help-overlay is-visible" role="dialog" aria-modal="false" aria-label="Istruzioni modalità freeroam">
      <div class="freeroam-help-panel">
        <button id="${FREEROAM_HELP_CLOSE_ID}" class="freeroam-help-close" type="button" aria-label="Chiudi istruzioni">
          <i class="material-icons">close</i>
        </button>
        <div class="freeroam-help-steps" data-step="0">
          <div class="freeroam-help-step is-active" data-step-index="0">
            <h2 class="freeroam-help-title">Muoviti utilizzando:</h2>
            <div class="freeroam-help-movement-layout">
              <div class="freeroam-help-keyset">
                <div class="freeroam-help-key freeroam-help-key--top">W</div>
                <div class="freeroam-help-key-row">
                  <div class="freeroam-help-key">A</div>
                  <div class="freeroam-help-key">S</div>
                  <div class="freeroam-help-key">D</div>
                </div>
                <div class="freeroam-help-caption">WASD</div>
              </div>
              <div class="freeroam-help-divider">o</div>
              <div class="freeroam-help-keyset">
                <div class="freeroam-help-key freeroam-help-key--top">↑</div>
                <div class="freeroam-help-key-row">
                  <div class="freeroam-help-key">←</div>
                  <div class="freeroam-help-key">↓</div>
                  <div class="freeroam-help-key">→</div>
                </div>
                <div class="freeroam-help-caption">Frecce direzionali</div>
              </div>
            </div>
          </div>
          <div class="freeroam-help-step" data-step-index="1">
            <h2 class="freeroam-help-title">Ruota la visuale:</h2>
            <div class="freeroam-help-hero-icon">
              <i class="material-icons">mouse</i>
            </div>
            <p class="freeroam-help-body">Clicca nella scena e muovi il mouse (oppure usa il trackpad) per guardarti intorno.</p>
          </div>
          <div class="freeroam-help-step" data-step-index="2">
            <h2 class="freeroam-help-title">Disattiva la rotazione:</h2>
            <div class="freeroam-help-esc-wrap">
              <div class="freeroam-help-key freeroam-help-key--esc">ESC</div>
            </div>
            <p class="freeroam-help-body">Premi una volta “Esc” per mostrare il cursore. Premilo due volte per uscire dal fullscreen.</p>
          </div>
        </div>
      </div>
      <div class="freeroam-help-actions">
        <button id="${FREEROAM_HELP_NEXT_ID}" class="freeroam-help-next" type="button">→</button>
      </div>
    </div>`

  widgetContainer.insertAdjacentHTML('beforeend', overlay)
}

function openFreeroamHelpOverlay() {
  document.getElementById(FREEROAM_HELP_OVERLAY_ID)?.classList.add('is-visible')
}

function closeFreeroamHelpOverlay() {
  document.getElementById(FREEROAM_HELP_OVERLAY_ID)?.classList.remove('is-visible')
}

function setFreeroamHelpStep(stepIndex) {
  const overlay = document.getElementById(FREEROAM_HELP_OVERLAY_ID)
  if (!overlay) return

  const steps = Array.from(overlay.querySelectorAll('.freeroam-help-step'))
  const boundedStepIndex = Math.max(0, Math.min(stepIndex, steps.length - 1))
  overlay.querySelector('.freeroam-help-steps')?.setAttribute('data-step', String(boundedStepIndex))

  steps.forEach((step, index) => {
    step.classList.toggle('is-active', index === boundedStepIndex)
  })

  const nextButton = document.getElementById(FREEROAM_HELP_NEXT_ID)
  if (!nextButton) return

  if (boundedStepIndex === steps.length - 1) {
    nextButton.textContent = 'Inizia il tour'
    nextButton.classList.add('freeroam-help-next--start')
  } else {
    nextButton.textContent = '→'
    nextButton.classList.remove('freeroam-help-next--start')
  }
}

function goToNextFreeroamHelpStep() {
  const stepsContainer = document.querySelector('.freeroam-help-steps')
  if (!stepsContainer) return

  const currentStep = Number(stepsContainer.getAttribute('data-step') || '0')
  const stepsCount = stepsContainer.querySelectorAll('.freeroam-help-step').length

  if (currentStep >= stepsCount - 1) {
    closeFreeroamHelpOverlay()
    setFreeroamHelpStep(0)
    return
  }

  setFreeroamHelpStep(currentStep + 1)
}

function bindFreeroamHelpEvents(currentMode) {
  const freeroamHelpButton = document.getElementById(FREEROAM_HELP_BUTTON_ID)
  const freeroamHelpCloseButton = document.getElementById(FREEROAM_HELP_CLOSE_ID)

  if (freeroamHelpButton && shouldShowFreeroamHelp(currentMode) && !freeroamHelpButton.dataset.bound) {
    freeroamHelpButton.addEventListener('click', () => {
      openFreeroamHelpOverlay()
      setFreeroamHelpStep(0)
    })
    freeroamHelpButton.dataset.bound = 'true'
  }

  if (
    freeroamHelpCloseButton &&
    shouldShowFreeroamHelp(currentMode) &&
    !freeroamHelpCloseButton.dataset.bound
  ) {
    freeroamHelpCloseButton.addEventListener('click', () => {
      closeFreeroamHelpOverlay()
      setFreeroamHelpStep(0)
    })
    freeroamHelpCloseButton.dataset.bound = 'true'
  }

  const freeroamHelpNextButton = document.getElementById(FREEROAM_HELP_NEXT_ID)
  if (
    freeroamHelpNextButton &&
    shouldShowFreeroamHelp(currentMode) &&
    !freeroamHelpNextButton.dataset.bound
  ) {
    freeroamHelpNextButton.addEventListener('click', () => {
      goToNextFreeroamHelpStep()
    })
    freeroamHelpNextButton.dataset.bound = 'true'
  }
}

function setupJoystick() {
  const zone = document.getElementById('joystick-zone')
  if (!zone) return

  const stopEvent = e => {
    e.stopPropagation()
  }

  zone.addEventListener('touchstart', stopEvent, { passive: false })
  zone.addEventListener('touchmove', stopEvent, { passive: false })
  zone.addEventListener('touchend', stopEvent, { passive: false })
  zone.addEventListener('pointerdown', stopEvent, { passive: false })

  const options = {
    zone: document.getElementById('joystick-zone'),
    mode: 'static',
    position: { left: '75px', top: '75px' },
    color: 'white',
    size: 100,
  }

  joystickManager = nipplejs.create(options)
  joystickManager.on('move', (evt, data) => {
    const forward = data.vector.y
    const side = data.vector.x

    keyStates['KeyW'] = forward > 0.2
    keyStates['KeyS'] = forward < -0.2
    keyStates['KeyA'] = side < -0.2
    keyStates['KeyD'] = side > 0.2
  })

  joystickManager.on('end', () => {
    keyStates['KeyW'] = false
    keyStates['KeyS'] = false
    keyStates['KeyA'] = false
    keyStates['KeyD'] = false
  })
}

function createJoystickIfMissing() {
  const widgetContainer = document.getElementById('widgetContainer')
  const zoneExists = document.getElementById('joystick-zone')
  if (!widgetContainer || zoneExists) return

  widgetContainer.insertAdjacentHTML('beforeend', '<div id="joystick-zone"></div>')
  setupJoystick()
}

function destroyJoystickIfPresent() {
  const zoneExists = document.getElementById('joystick-zone')
  if (!zoneExists) return

  if (joystickManager) {
    joystickManager.destroy()
    joystickManager = null
  }

  zoneExists.remove()
  document.getElementById('nipple_0_0')?.remove()
}

function destroyFreeroamHelpIfPresent() {
  document.getElementById(FREEROAM_HELP_OVERLAY_ID)?.remove()
  document.getElementById('freeroam-help-trigger')?.remove()
}

function updateFreeroamUI(currentMode) {
  if (currentMode !== 'freeroam') return

  const shouldShowJoystick = isTouchDevice()
  const shouldShowHelp = shouldShowFreeroamHelp(currentMode)

  if (shouldShowJoystick) {
    createJoystickIfMissing()
  } else {
    destroyJoystickIfPresent()
  }

  if (shouldShowHelp) {
    if (!document.getElementById('freeroam-help-trigger')) {
      const widgetContainer = document.getElementById('widgetContainer')
      if (widgetContainer) {
        widgetContainer.insertAdjacentHTML(
          'beforeend',
          `
    <div id="freeroam-help-trigger" class="${is360ViewerDevice()}">
      <button id="${FREEROAM_HELP_BUTTON_ID}" class="titleContentToggle" type="button" aria-label="Apri istruzioni">
        <span class="freeroam-help-trigger-label" aria-hidden="true">?</span>
      </button>
    </div>`
        )
      }
    }

    createFreeroamHelpOverlay()
    bindFreeroamHelpEvents(currentMode)
  } else {
    destroyFreeroamHelpIfPresent()
  }
}

function setModeButtonState(selectedId) {
  ;[
    ENABLE_TEXTURES_BUTTON_ID,
    ENABLE_SIMPLE_MATERIAL_BUTTON_ID,
    ENABLE_WIREFRAME_BUTTON_ID,
  ].forEach(id => {
    const element = document.getElementById(id)
    if (element) {
      element.classList.toggle(SELECTED_BUTTON_CLASS, id === selectedId)
    }
  })
}

function updateMaterialState(newState) {
  textureDisabled = newState.textureDisabled
  simpleMaterialDisabled = newState.simpleMaterialDisabled
  wireframeDisabled = newState.wireframeDisabled
}

function isSimpleMaterialModeActive() {
  return simpleMaterialDisabled === false
}

function getFreeroamLightButtons() {
  return [FLASHLIGHT_TOGGLE_BUTTON_ID, TORCH_TOGGLE_BUTTON_ID, CANDLE_TOGGLE_BUTTON_ID]
    .map(id => document.getElementById(id))
    .filter(Boolean)
}

function clearFreeroamLightSelections() {
  getFreeroamLightButtons().forEach(button => {
    button.classList.remove(SELECTED_BUTTON_CLASS)
  })
}

function setFreeroamLightsEnabled(enabled) {
  getFreeroamLightButtons().forEach(button => {
    button.disabled = !enabled
    button.setAttribute('aria-disabled', String(!enabled))
    button.classList.toggle('is-disabled', !enabled)
    if (!enabled) {
      button.setAttribute('title', LIGHTS_DISABLED_TOOLTIP)
    } else {
      button.removeAttribute('title')
    }
  })
}

function getRequestedFreeroamLightButtonId() {
  const lightParam = new URLSearchParams(window.location.search).get(FREEROAM_LIGHT_PARAM)
  return FREEROAM_LIGHT_OPTIONS[lightParam] || null
}

function applyInitialFreeroamLightSelection() {
  const requestedLight = getRequestedFreeroamLightButtonId()
  if (!requestedLight) return

  const requestedButton = document.getElementById(requestedLight.buttonId)
  if (!requestedButton || requestedButton.disabled) return

  setFreeroamLight(requestedLight.lightType)
  clearFreeroamLightSelections()
  requestedButton.classList.add(SELECTED_BUTTON_CLASS)
}

function mouseup() {
  if (dynamicLightsEnabled) {
    lampStatus(true)
  } else {
    lampStatus(false)
  }
  document.removeEventListener('mouseup', mouseup)
}

function lampStatus(value) {
  const container = document.getElementById('widgetContainer')
  if (value) {
    container.style.cursor = 'url("' + lampIcon + '") 20 20,move'
  } else {
    container.style.cursor = 'auto'
    disableDynamicLights()
  }
}

function mousedown() {
  document.addEventListener('mouseup', mouseup, false)
}

function handleButtonsEvents(config) {
  const { resetFunction, currentMode, toggleFlashlight, toggleTorch, toggleCandle } = config

  const homeButton = document.getElementById('home')
  if (homeButton && resetFunction) {
    homeButton.addEventListener('click', () => {
      resetFunction()
    })
  }

  bindFreeroamHelpEvents(currentMode)

  const snapshotButton = document.getElementById(SNAPSHOT_BUTTON_ID)
  if (snapshotButton) {
    snapshotButton.addEventListener('click', () => {
      const canvas = document.querySelector('#widgetContainer canvas')
      if (!canvas) return
      try {
        const link = document.createElement('a')
        link.href = canvas.toDataURL('image/png')
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
        link.download = `snapshot-${timestamp}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (e) {
        console.error('Snapshot failed:', e)
      }
    })
  }

  // Handle dynamic light button for orbit mode
  if (currentMode === 'orbit') {
    const dynamicLightsButton = document.getElementById(DYNAMIC_LIGHTS_TOGGLE_BUTTON_ID)
    if (dynamicLightsButton) {
      dynamicLightsButton.addEventListener('click', () => {
        dynamicLightsEnabled = !dynamicLightsEnabled
        dynamicLightsButton.classList.toggle(SELECTED_BUTTON_CLASS, dynamicLightsEnabled)
        lampStatus(dynamicLightsEnabled)
      })
    }
    document.addEventListener('mousedown', mousedown, false)
    document.addEventListener('pointerdown', mousedown, false)
  }

  // Handle flashlight, torch and candle buttons for freeroam mode
  if (currentMode === 'freeroam') {
    const buttons = [
      { id: FLASHLIGHT_TOGGLE_BUTTON_ID, toggle: toggleFlashlight },
      { id: TORCH_TOGGLE_BUTTON_ID, toggle: toggleTorch },
      { id: CANDLE_TOGGLE_BUTTON_ID, toggle: toggleCandle },
    ]

    const buttonElements = buttons.map(({ id }) => document.getElementById(id))

    // Helper to deselect all other buttons except the active one
    function deselectOthers(activeIndex) {
      buttonElements.forEach((el, i) => {
        if (el && i !== activeIndex) {
          el.classList.remove(SELECTED_BUTTON_CLASS)
        }
      })
    }

    buttons.forEach(({ toggle }, index) => {
      const btn = buttonElements[index]
      if (!btn) return

      btn.addEventListener('click', () => {
        const isOn = toggle()
        btn.classList.toggle(SELECTED_BUTTON_CLASS, isOn)

        if (isOn) {
          deselectOthers(index)
        }

        lampStatus(false)
      })
    })
  }

  const modeButtons = [
    {
      id: ENABLE_TEXTURES_BUTTON_ID,
      state: { textureDisabled: false, simpleMaterialDisabled: true, wireframeDisabled: true },
    },
    {
      id: ENABLE_SIMPLE_MATERIAL_BUTTON_ID,
      state: { textureDisabled: true, simpleMaterialDisabled: false, wireframeDisabled: true },
    },
    {
      id: ENABLE_WIREFRAME_BUTTON_ID,
      state: { textureDisabled: true, simpleMaterialDisabled: true, wireframeDisabled: false },
    },
  ]

  modeButtons.forEach(button => {
    document.getElementById(button.id)?.addEventListener('click', () => {
      setModeButtonState(button.id)
      updateMaterialState(button.state)
      applyMainLighting()

      if (currentMode !== 'freeroam') return

      const lightsAllowed = !isSimpleMaterialModeActive()
      const hadActiveFreeroamLights = hasAnyFreeroamLightActive()
      setFreeroamLightsEnabled(lightsAllowed)

      if (!lightsAllowed && hadActiveFreeroamLights) {
        disableAllFreeroamLights()
        clearFreeroamLightSelections()
      }
    })
  })
}

let cursorLight = null

function handleLights(lights, currentMode, camera, scene) {
  function updateLightPosition(clientX, clientY) {
    if (!dynamicLightsEnabled || currentMode !== 'orbit') return

    if (!cursorLight) {
      const radius = getModelRadius()
      const lightDistance = radius * 1.5
      const intensity = Math.min(2, Math.max(0.8, radius * 0.15))

      cursorLight = new THREE.PointLight(0xffeeaa, intensity, lightDistance, 2)
      cursorLight.castShadow = false
      cursorLight.userData.isDynamicCursorLight = true

      if (scene) scene.add(cursorLight)

      const hemi = scene.getObjectByName('orbit_hemi_light')
      if (hemi) hemi.intensity = 0.6
    }

    const mouse = new THREE.Vector2(
      (clientX / window.innerWidth) * 2 - 1,
      -(clientY / window.innerHeight) * 2 + 1
    )

    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(mouse, camera)
    const intersect = raycaster.intersectObjects(scene.children, true)[0]

    if (intersect) {
      cursorLight.position.copy(intersect.point)
      cursorLight.position.add(intersect.face.normal.multiplyScalar(0.05))
    } else {
      const pos = new THREE.Vector3()
      raycaster.ray.at(getModelRadius(), pos)
      cursorLight.position.copy(pos)
    }
  }

  const onMouseMove = e => updateLightPosition(e.clientX, e.clientY)

  const onTouchMove = e => {
    if (e.touches.length > 0) {
      if (dynamicLightsEnabled && currentMode === 'orbit') {
        if (e.cancelable) e.preventDefault()
        updateLightPosition(e.touches[0].clientX, e.touches[0].clientY)
      }
    }
  }

  if (currentMode === 'orbit') {
    document.addEventListener('mousemove', onMouseMove, false)
    document.addEventListener('touchmove', onTouchMove, { passive: false })
    document.addEventListener('keyup', e => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        disableDynamicLights()
      }
    })
  }
}

function disableDynamicLights() {
  dynamicLightsEnabled = false
  const button = document.getElementById(DYNAMIC_LIGHTS_TOGGLE_BUTTON_ID)
  if (button) button.classList.remove(SELECTED_BUTTON_CLASS)
  if (cursorLight && cursorLight.parent) {
    cursorLight.parent.remove(cursorLight)
    cursorLight = null
  }
}

export async function setup(config) {
  const {
    lights,
    resetFunction,
    currentMode,
    toggleFlashlight,
    toggleTorch,
    toggleCandle,
    camera,
    scene,
    canSnapshot,
  } = config

  createControlsDom(currentMode, canSnapshot)
  updateFreeroamUI(currentMode)
  if (currentMode === 'freeroam') {
    setFreeroamLightsEnabled(!isSimpleMaterialModeActive())
    applyInitialFreeroamLightSelection()
    setFreeroamHelpStep(0)
  } else if (currentMode === 'orbit') {
    const dynamicLightsButton = document.getElementById(DYNAMIC_LIGHTS_TOGGLE_BUTTON_ID)
    if (dynamicLightsButton) {
      dynamicLightsButton.classList.toggle(SELECTED_BUTTON_CLASS, dynamicLightsEnabled)
    }
  }
  let resizeTimeout
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(() => {
      updateFreeroamUI(currentMode)
    }, 250)
  })

  setupFullscreenButton({ currentMode })

  handleButtonsEvents({
    resetFunction,
    currentMode,
    toggleFlashlight,
    toggleTorch,
    toggleCandle,
  })

  handleLights(lights, currentMode, camera, scene)
}
